import React, { useState } from 'react'
import { ResizeVertical } from 'components/icons'
import Tooltip from 'components/tooltip'

export const MIN_EMBED_HEIGHT = 150
const HEIGHT_STEP = 10
const RESIZE_HINT = 'Drag to resize'
// Tooltips normally float above everything (z-[9999]). This one shows whenever the pointer is over the
// embed, so keep it under the editor's menus (zIndex 1000) — an open Edit dialog must cover the hint.
const HINT_LAYER = 'z-[999]'

/** The height after dragging `deltaY` px from `startHeight`, snapped to HEIGHT_STEP and never below the minimum. */
export const getResizedHeight = (startHeight: number, deltaY: number): number =>
  Math.max(MIN_EMBED_HEIGHT, Math.round((startHeight + deltaY) / HEIGHT_STEP) * HEIGHT_STEP)

interface EmbedResizeHandleProps {
  height: number
  // Pixels of height change per pixel dragged — a dashboard spreads the drag across its rows.
  dragScale?: number
  // Appended to the live height readout, e.g. " per row" for a dashboard's row height.
  heightSuffix?: string
  // The pointer is anywhere over the embedded view: shows the "Drag to resize" hint.
  viewHovered?: boolean
  onResize: (height: number) => void
  onResizeEnd: (height: number) => void
}

/**
 * Drag-to-resize for embedded tables and dashboards: a bar along the bottom edge with a centred pill,
 * plus a grip in the bottom-right corner, and a "Drag to resize" hint at the bottom edge while
 * `viewHovered` (the pointer is anywhere over the embed — see usePointerWithin). Render inside a
 * `relative` container. In edit mode the editor turns pointer events off for these embeds
 * (style.scss), so the handles opt back in with pointer-events-auto. `onResize` fires while dragging so the embed can follow the pointer;
 * `onResizeEnd` fires once on release with the height to save.
 */
const EmbedResizeHandle: React.FC<EmbedResizeHandleProps> = ({
  height,
  dragScale = 1,
  heightSuffix = '',
  viewHovered = false,
  onResize,
  onResizeEnd
}) => {
  const [hovered, setHovered] = useState(false)
  // The height being dragged to, shown beside the grip; undefined when not dragging.
  const [liveHeight, setLiveHeight] = useState<number>()
  const active = hovered || liveHeight !== undefined
  // Hovering anywhere on the view shows the hint at its bottom edge. The pointer slips on and off the
  // moving handle mid-drag, so the hint stays hidden until it's released.
  const hintVisible = liveHeight === undefined && (viewHovered || hovered)

  const startDrag = (event: React.MouseEvent) => {
    // Keep the editor's selection and focus, and stop the click reaching the embedded view.
    event.preventDefault()
    event.stopPropagation()

    const startY = event.clientY
    let latestHeight = height
    setLiveHeight(height)

    const onMove = (moveEvent: MouseEvent) => {
      latestHeight = getResizedHeight(height, (moveEvent.clientY - startY) * dragScale)
      setLiveHeight(latestHeight)
      onResize(latestHeight)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      setLiveHeight(undefined)
      onResizeEnd(latestHeight)
    }

    // Keep the resize cursor while the pointer is over the embedded view mid-drag.
    document.body.style.cursor = 'ns-resize'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const hoverProps = {
    onMouseDown: startDrag,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false)
  }

  return (
    <>
      <Tooltip content={RESIZE_HINT} visible={hintVisible} className={HINT_LAYER}>
        <div
          data-testid="embed-resize-bar"
          role="separator"
          aria-orientation="horizontal"
          aria-label={RESIZE_HINT}
          className="absolute left-0 right-0 -bottom-1.5 h-3 z-[3] pointer-events-auto cursor-ns-resize"
          {...hoverProps}
        >
          <div
            className={`absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full transition-colors ${
              active ? 'bg-blue' : 'bg-transparent'
            }`}
          />
          <div
            data-testid="embed-resize-pill"
            className={`absolute left-1/2 top-1/2 w-10 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors ${
              active ? 'bg-blue' : 'bg-faint'
            }`}
          />
        </div>
      </Tooltip>
      <div
        data-testid="embed-resize-grip"
        aria-hidden
        className={`absolute right-1 bottom-1 z-[4] pointer-events-auto flex items-center justify-center w-5 h-5 rounded cursor-ns-resize bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.18)] transition-colors ${
          active ? 'text-blue' : 'text-muted'
        }`}
        {...hoverProps}
      >
        <ResizeVertical className="w-3.5 h-3.5" />
      </div>
      {liveHeight !== undefined && (
        <div
          data-testid="embed-resize-height"
          className="absolute right-7 bottom-1 z-[4] px-1.5 py-0.5 rounded text-xs tabular-nums bg-body text-surface pointer-events-none"
        >
          {`${liveHeight}px${heightSuffix}`}
        </div>
      )}
    </>
  )
}

export default EmbedResizeHandle
